import React, { useState, useEffect } from 'react';
import { FileText, Download, Trash2, RefreshCw, Plus, Calendar, Filter, CheckCircle, Clock, XCircle, Loader } from 'lucide-react';
import { reportsService } from 'api/services/reports.service';
import { organizationsService } from 'api/services/organizations.service';
import { CustomReport, CreateReportRequest, PaginatedResponse } from 'types';
import { useApi } from 'hooks/useApi';
import { useAuth } from 'hooks/useAuth';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';
import Pagination from 'components/Common/Pagination';
import OrganizationFilter from 'components/Filters/OrganizationFilter';
import { formatTimestamp, formatBytes, getDefaultDateRange } from 'utils/helpers';
import { ITEMS_PER_PAGE, FEATURE_FLAGS } from 'utils/constants';

const CustomReports: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const currentRole = (currentOrganization?.role || user?.role)?.toLowerCase();

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrgId, setSelectedOrgId] = useState<string | 'all'>(
    FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA
      ? (currentOrganization?.id || '')
      : 'all'
  );

  // Create report form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [reportType, setReportType] = useState<'ai_calls' | 'transferred_calls' | 'all_calls'>('all_calls');
  const defaultRange = getDefaultDateRange(7); // Default to 7 days
  const [dateFrom, setDateFrom] = useState(defaultRange.dateFrom);
  const [dateTo, setDateTo] = useState(defaultRange.dateTo);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string>('');

  // Determine organization ID to pass to API
  const getOrganizationId = () => {
    // For master_admin, super_admin, and service_admin: MUST select from dropdown, no fallback
    if (currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') {
      if (selectedOrgId && selectedOrgId !== 'all' && selectedOrgId !== '') {
        return selectedOrgId;
      }
      return undefined;
    }

    // For service_user: use dropdown if selected, otherwise use currentOrganization
    if (currentRole === 'service_user') {
      if (selectedOrgId && selectedOrgId !== 'all' && selectedOrgId !== '') {
        return selectedOrgId;
      }
      if (currentOrganization?.id && currentOrganization.id !== 'synthetic-org') {
        return currentOrganization.id;
      }
      return undefined;
    }

    // For regular users (admin, operator, etc): use currentOrganization
    if (currentOrganization?.id && currentOrganization.id !== 'synthetic-org') {
      return currentOrganization.id;
    }
    return undefined;
  };

  // service_admin belongs here with the other two. getOrganizationId() already
  // treats all three the same — it returns undefined until an organisation is
  // picked — but only master and super admins were ever ASKED to pick one. A
  // service provider therefore got no prompt and no organisationId, and the
  // backend's no-organisation path reads the LOCAL call tables rather than
  // federating to the switches, which is where this estate's calls actually
  // live. The page came back empty and said nothing.
  const needsOrgSelection = FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA &&
    (currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') &&
    (!selectedOrgId || selectedOrgId === 'all' || selectedOrgId === '');

  const { data, loading, error, refetch } = useApi<PaginatedResponse<CustomReport>>(
    () => {
      if (needsOrgSelection) {
        return Promise.resolve({ success: true, data: { data: [], total: 0, totalPages: 0, currentPage: 1 } });
      }
      return reportsService.getReports({
        page: currentPage,
        perPage: ITEMS_PER_PAGE,
        organizationId: getOrganizationId()
      });
    },
    [currentPage, selectedOrgId, currentOrganization?.id, needsOrgSelection]
  );

  const handleCreateReport = async () => {
    setIsCreating(true);
    setCreateError('');

    console.log('DEBUG: selectedOrgId =', selectedOrgId);
    console.log('DEBUG: currentOrganization?.id =', currentOrganization?.id);
    console.log('DEBUG: currentRole =', currentRole);

    const orgId = getOrganizationId();
    console.log('DEBUG: getOrganizationId() returned =', orgId);

    // Validate that organization ID exists
    if (!orgId) {
      const errorMsg = (currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin')
        ? 'Please select an organization from the dropdown before creating a report'
        : 'Unable to determine organization. Please contact support.';
      setCreateError(errorMsg);
      setIsCreating(false);
      return;
    }

    const request: CreateReportRequest = {
      report_type: reportType,
      date_from: dateFrom,
      date_to: dateTo,
      organization_id: orgId
    };

    console.log('Creating report with request:', request);

    const response = await reportsService.createReport(request);

    if (response.success) {
      setShowCreateForm(false);
      setIsCreating(false);
      refetch();
      // Reset form
      const newRange = getDefaultDateRange(7);
      setDateFrom(newRange.dateFrom);
      setDateTo(newRange.dateTo);
      setReportType('all_calls');
    } else {
      setCreateError(response.error || 'Failed to create report');
      setIsCreating(false);
    }
  };

  const handleDownload = async (report: CustomReport) => {
    try {
      await reportsService.downloadReport(report.id, report.report_name);
    } catch (error: any) {
      alert(error.message || 'Failed to download report');
    }
  };

  const handleDelete = async (report: CustomReport) => {
    if (!window.confirm(`Are you sure you want to delete report "${report.report_name}"?`)) {
      return;
    }

    const response = await reportsService.deleteReport(report.id);
    if (response.success) {
      refetch();
    } else {
      alert(response.error || 'Failed to delete report');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'processing':
        return <Loader className="w-4 h-4 text-blue-600 animate-spin" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getReportTypeName = (type: string) => {
    switch (type) {
      case 'ai_calls':
        return 'AI Calls';
      case 'transferred_calls':
        return 'Transferred Calls';
      case 'all_calls':
        return 'All Calls';
      default:
        return type;
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error && !needsOrgSelection) return <ErrorMessage message={error} onRetry={refetch} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
          Custom Reports {data ? `(${data.total})` : ''}
        </h1>
        <div className="flex gap-2">
          <button
            onClick={refetch}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Report
          </button>
        </div>
      </div>

      {/* Organization Filter */}
      {(currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <OrganizationFilter
            value={selectedOrgId === 'all' ? '' : selectedOrgId}
            onChange={(value) => {
              console.log('DEBUG: OrganizationFilter onChange - value =', value);
              if (FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA) {
                setSelectedOrgId(value || selectedOrgId);
              } else {
                setSelectedOrgId(value || 'all');
              }
              console.log('DEBUG: OrganizationFilter onChange - after setSelectedOrgId, value will be =', value || (FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA ? selectedOrgId : 'all'));
              setCurrentPage(1);
            }}
            className="w-full md:w-96"
            requireSelection={FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA}
            allowServiceProviderSelection={true}
          />
        </div>
      )}

      {/* Create Report Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full">
            <div className="p-6 border-b dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Create Custom Report</h3>
            </div>
            <div className="p-6 space-y-4">
              {createError && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-300 text-sm">
                  {createError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Report Type
                </label>
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all_calls">All Calls</option>
                  <option value="ai_calls">AI Calls Only</option>
                  <option value="transferred_calls">Transferred Calls Only</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    From Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    To Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>
            <div className="p-6 border-t dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setCreateError('');
                }}
                disabled={isCreating}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateReport}
                disabled={isCreating}
                className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-50"
              >
                {isCreating && <Loader className="w-4 h-4 animate-spin" />}
                {isCreating ? 'Creating...' : 'Create Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reports List */}
      {!needsOrgSelection && data && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Report Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Date Range
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {data.data.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-500" />
                        {report.report_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                      {getReportTypeName(report.report_type)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-gray-400" />
                        <span className="text-xs">
                          {formatTimestamp(report.date_from)} - {formatTimestamp(report.date_to)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                        {getStatusIcon(report.status)}
                        {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                      {formatTimestamp(report.created_at)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                      {report.file_size ? formatBytes(report.file_size) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center gap-2">
                        {report.status === 'completed' && (
                          <button
                            onClick={() => handleDownload(report)}
                            className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                            title="Download Report"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(report)}
                          className="text-red-600 hover:text-red-800 hover:underline flex items-center gap-1"
                          title="Delete Report"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {data.data.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <FileText className="w-12 h-12 text-gray-400" />
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">No Reports Found</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Create your first custom report to get started.
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {data.data.length > 0 && (
            <div className="p-4 border-t dark:border-gray-700">
              <Pagination
                currentPage={currentPage}
                totalPages={data.totalPages}
                onPageChange={setCurrentPage}
                totalItems={data.total}
                itemsPerPage={ITEMS_PER_PAGE}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomReports;
